import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, BookOpen } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
import { WhatsAppButton } from './WhatsAppButton';
import { getPostsForLanguage } from '../data/blog';
import { format } from 'date-fns';
import { ptBR, enUS, es as esLocale, fr as frLocale } from 'date-fns/locale';

const dateLocaleMap = { pt: ptBR, en: enUS, es: esLocale, fr: frLocale };

export const BlogList = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'en';
  const dateLocale = dateLocaleMap[currentLang] || enUS;
  const posts = getPostsForLanguage(currentLang);

  // SEO: title and meta description for the blog index
  useEffect(() => {
    document.title = t('blog.list_title');
    const desc = document.head.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', t('blog.list_description'));
  }, [t, currentLang]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-amber-700 via-amber-600 to-amber-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-5">
              <BookOpen className="w-4 h-4" />
              <span className="text-sm font-medium uppercase tracking-wider">{t('blog.badge')}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="blog-list-title">
              {t('blog.list_title')}
            </h1>
            <p className="text-lg md:text-xl text-amber-50/90 max-w-2xl mx-auto">
              {t('blog.list_subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Posts grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {posts.map((post) => (
              <article
                key={post.slug}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300"
                data-testid={`blog-card-${post.slug}`}
              >
                <Link to={`/${currentLang}/blog/${post.slug}`}>
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 bg-amber-600 text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full">
                      {t(`blog.categories.${post.category}`)}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {format(new Date(post.publishDate), 'PP', { locale: dateLocale })}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {post.readMinutes} min
                      </span>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-amber-700 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-sm text-gray-600 line-clamp-3 mb-4">{post.excerpt}</p>
                    <div className="inline-flex items-center gap-1 text-amber-700 text-sm font-semibold group-hover:gap-2 transition-all">
                      {t('blog.read_more')}
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};
